import AnalysisCandleChart from '@/pages/forex-analysis/components/AnalysisCandleChart';
import { analysisChartCandles, analysisHero, chartLevels } from '@/mocks/forexAnalysisPage';

export default function Hero() {
  return (
    <section className="relative overflow-hidden border-b border-background-200 bg-background-50">
      <div className="pointer-events-none absolute inset-0 grid-lines opacity-40" />
      <div className="pointer-events-none absolute -right-24 -top-24 h-80 w-80 rounded-full bg-primary-100/60 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-24 left-1/3 h-64 w-64 rounded-full bg-accent-100/50 blur-3xl" />

      <div className="relative mx-auto w-full max-w-7xl px-4 pb-14 pt-10 md:px-6 md:pb-20 md:pt-14 lg:px-10">
        <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-[1.02fr_0.98fr] lg:gap-14">
          <div className="min-w-0">
            <span className="inline-flex items-center gap-2 rounded-full border border-background-300 bg-background-50/80 px-3 py-1.5 text-xs font-semibold text-primary-700">
              <span className="h-px w-5 bg-accent-500" />
              {analysisHero.eyebrow}
            </span>

            <h1 className="mt-5 text-balance font-heading text-3xl font-semibold leading-[1.16] text-foreground-950 sm:text-4xl lg:text-[50px]">
              เรียนรู้การวิเคราะห์ Forex
              <span className="mt-1 block text-primary-700">สำหรับมือใหม่</span>
            </h1>

            <p className="mt-5 max-w-xl text-sm leading-relaxed text-foreground-700 md:text-base">
              {analysisHero.description}
            </p>

            <div className="mt-7 flex flex-col gap-3 sm:flex-row">
              <a
                href="#learning-path"
                className="inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
              >
                {analysisHero.primaryCta}
                <i className="ri-arrow-down-line text-base" aria-hidden="true" />
              </a>
              <a
                href="#tutorial-library"
                className="inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-5 py-3 text-sm font-semibold text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-700"
              >
                {analysisHero.secondaryCta}
                <i className="ri-layout-grid-line text-base" aria-hidden="true" />
              </a>
            </div>

            <dl className="mt-8 flex flex-wrap items-center gap-x-8 gap-y-4 border-t border-background-200 pt-6">
              {analysisHero.highlights.map((item) => (
                <div key={item.label} className="flex items-baseline gap-2">
                  <dt className="font-num text-xl font-semibold text-foreground-950">{item.value}</dt>
                  <dd className="text-xs text-foreground-500">{item.label}</dd>
                </div>
              ))}
            </dl>
          </div>

          <div className="min-w-0">
            <figure className="rounded-xl border border-background-200 bg-background-50 p-3 md:p-4">
              <div className="flex flex-wrap items-center justify-between gap-2 border-b border-background-200 pb-3">
                <div className="flex items-center gap-2">
                  <span className="font-num text-sm font-semibold text-foreground-950">{analysisHero.chart.symbol}</span>
                  <span className="rounded-sm bg-background-100 px-1.5 py-0.5 font-num text-[10px] font-semibold text-foreground-600">
                    {analysisHero.chart.timeframe}
                  </span>
                  <span className="flex items-center gap-1 text-[11px] font-medium text-primary-700">
                    <i className="ri-line-chart-line text-sm" aria-hidden="true" />
                    แนวโน้มขาขึ้น
                  </span>
                </div>
                <span className="rounded-full bg-accent-50 px-2 py-0.5 text-[10px] font-medium text-accent-800">
                  ภาพตัวอย่างเพื่อการสอน
                </span>
              </div>

              <div className="relative mt-3 h-[240px] w-full sm:h-[290px] lg:h-[320px]">
                <AnalysisCandleChart
                  candles={analysisChartCandles}
                  support={chartLevels.support}
                  resistance={chartLevels.resistance}
                  ariaLabel="กราฟแท่งเทียนตัวอย่างสำหรับการเรียนรู้ พร้อมเส้นแนวโน้มขาขึ้น แนวรับ และแนวต้าน"
                />
                <span className="pointer-events-none absolute left-[44%] top-[18%] rounded-sm border border-accent-200 bg-background-50/95 px-2 py-0.5 text-[10px] font-semibold text-accent-800">
                  ราคาผ่านแนวต้าน
                </span>
              </div>

              <figcaption className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-background-200 pt-3">
                <span className="flex items-center gap-1.5 text-[11px] text-foreground-600">
                  <span className="h-2.5 w-2.5 rounded-sm bg-primary-500" aria-hidden="true" />
                  แท่งเทียนขึ้น
                </span>
                <span className="flex items-center gap-1.5 text-[11px] text-foreground-600">
                  <span className="h-2.5 w-2.5 rounded-sm bg-accent-500" aria-hidden="true" />
                  แท่งเทียนลง
                </span>
                <span className="flex items-center gap-1.5 text-[11px] text-foreground-600">
                  <span className="h-[2px] w-4 bg-accent-500" aria-hidden="true" />
                  เส้นแนวโน้มราคา
                </span>
                <span className="flex items-center gap-1.5 text-[11px] text-foreground-600">
                  <span className="h-0 w-4 border-t border-dashed border-primary-500" aria-hidden="true" />
                  แนวรับ–แนวต้าน
                </span>
              </figcaption>
            </figure>

            <p className="mt-3 px-1 text-[11px] leading-relaxed text-foreground-500">
              กราฟนี้สร้างขึ้นเพื่ออธิบายหลักการอ่านกราฟเท่านั้น ไม่ใช่ราคาเรียลไทม์ ไม่ใช่สัญญาณซื้อขาย
              และไม่ใช่การรับประกันผลลัพธ์ใด ๆ
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}